// Mock Data Generator for Access Project Management
import type {
  Client,
  Employee,
  Project,
  ProjectPhase,
  Task,
  TimeEntry,
  Expense,
  Milestone,
  ChangeRequest,
  WIPReport,
  UtilisationData,
  PortfolioSummary,
  IntegrationStatus,
  ResourceAllocation,
  CompletionPercentAuditEntry,
} from '@/types';
import type { Company } from '@/types/company';
import { getBudgetDataByProjectId } from './mock-data/budget-mock-data';
import type { BillingMilestone } from '@/types/budget.types';

// UK Clients
export const mockClients: Client[] = [
  { 
    id: 1, 
    name: 'Deloitte UK', 
    code: 'DEL-001',
    industry: 'Consulting', 
    contactName: 'Sarah Mitchell', 
    contactEmail: 'sarah.mitchell@deloitte.co.uk',
    vatNumber: 'GB123456789',
    invoiceAddress: {
      line1: '1 New Street Square',
      line2: '',
      city: 'London',
      county: 'Greater London',
      postcode: 'EC4A 3HQ',
      country: 'United Kingdom'
    },
    deliveryAddress: {
      line1: '1 New Street Square',
      line2: '',
      city: 'London',
      county: 'Greater London',
      postcode: 'EC4A 3HQ',
      country: 'United Kingdom'
    }
  },
  { id: 2, name: 'KPMG London', code: 'KPMG-002', industry: 'Accounting', contactName: 'James Thompson', contactEmail: 'james.thompson@kpmg.co.uk' },
  { id: 3, name: 'Arup Group', code: 'ARUP-003', industry: 'Engineering', contactName: 'Emma Richardson', contactEmail: 'emma.richardson@arup.com' },
  { id: 4, name: 'Clifford Chance', code: 'CLIF-004', industry: 'Legal', contactName: 'Oliver Davies', contactEmail: 'oliver.davies@cliffordchance.com' },
  { id: 5, name: 'Accenture UK', code: 'ACC-005', industry: 'IT Services', contactName: 'Sophie Williams', contactEmail: 'sophie.williams@accenture.com' },
  { id: 6, name: 'Foster + Partners', code: 'FOS-006', industry: 'Architecture', contactName: 'Thomas Brown', contactEmail: 'thomas.brown@fosterandpartners.com' },
  { id: 7, name: 'PwC Birmingham', code: 'PWC-007', industry: 'Consulting', contactName: 'Charlotte Evans', contactEmail: 'charlotte.evans@pwc.com' },
  { id: 8, name: 'Mott MacDonald', code: 'MOTT-008', industry: 'Engineering', contactName: 'William Taylor', contactEmail: 'william.taylor@mottmac.com' },
  { id: 9, name: 'Freshfields', code: 'FRESH-009', industry: 'Legal', contactName: 'Amelia Wilson', contactEmail: 'amelia.wilson@freshfields.com' },
  { id: 10, name: 'Capgemini UK', code: 'CAP-010', industry: 'IT Services', contactName: 'George Harris', contactEmail: 'george.harris@capgemini.com' },
  { id: 11, name: 'Atkins Global', code: 'ATK-011', industry: 'Engineering', contactName: 'Isabella Martin', contactEmail: 'isabella.martin@atkinsglobal.com' },
  { id: 12, name: 'EY Manchester', code: 'EY-012', industry: 'Accounting', contactName: 'Jack Robinson', contactEmail: 'jack.robinson@uk.ey.com' },
  { id: 13, name: 'Zaha Hadid Architects', code: 'ZAHA-013', industry: 'Architecture', contactName: 'Olivia Clark', contactEmail: 'olivia.clark@zaha-hadid.com' },
  { id: 14, name: 'BDO London', code: 'BDO-014', industry: 'Accounting', contactName: 'Harry Lewis', contactEmail: 'harry.lewis@bdo.co.uk' },
  { id: 15, name: 'Slaughter and May', code: 'SLAU-015', industry: 'Legal', contactName: 'Lily Walker', contactEmail: 'lily.walker@slaughterandmay.com' },
];

// Employees with UK names and roles
export const mockEmployees: Employee[] = [
  { id: 1, name: 'Alice Johnson', email: 'alice.johnson@company.co.uk', role: 'Senior Project Manager', department: 'Delivery', costRate: 65, billRate: 150, skills: ['Project Management', 'Agile', 'Risk Management'], photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400', isAvailable: true },
  { id: 2, name: 'Ben Carter', email: 'ben.carter@company.co.uk', role: 'Project Manager', department: 'Delivery', costRate: 55, billRate: 125, skills: ['Project Management', 'Stakeholder Management'], photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400', isAvailable: true },
  { id: 3, name: 'Catherine Davies', email: 'catherine.davies@company.co.uk', role: 'Senior Consultant', department: 'Consulting', costRate: 70, billRate: 160, skills: ['Business Analysis', 'Strategy', 'Change Management'], photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400', isAvailable: true },
  { id: 4, name: 'David Smith', email: 'david.smith@company.co.uk', role: 'Lead Developer', department: 'Technology', costRate: 60, billRate: 140, skills: ['React', 'Node.js', 'AWS'], photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400', isAvailable: true },
  { id: 5, name: 'Emma Wilson', email: 'emma.wilson@company.co.uk', role: 'Senior Developer', department: 'Technology', costRate: 55, billRate: 130, skills: ['React', 'TypeScript', 'Azure'], photo: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400', isAvailable: true },
  { id: 6, name: 'Frank Brown', email: 'frank.brown@company.co.uk', role: 'Business Analyst', department: 'Consulting', costRate: 50, billRate: 115, skills: ['Requirements Gathering', 'Process Mapping', 'SQL'], photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400', isAvailable: true },
  { id: 7, name: 'Grace Taylor', email: 'grace.taylor@company.co.uk', role: 'UX Designer', department: 'Design', costRate: 48, billRate: 110, skills: ['Figma', 'User Research', 'Prototyping'], photo: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400', isAvailable: true },
  { id: 8, name: 'Henry Evans', email: 'henry.evans@company.co.uk', role: 'Data Analyst', department: 'Analytics', costRate: 45, billRate: 105, skills: ['Python', 'Power BI', 'SQL'], photo: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400', isAvailable: true },
  { id: 9, name: 'Isla Roberts', email: 'isla.roberts@company.co.uk', role: 'QA Lead', department: 'Quality', costRate: 50, billRate: 115, skills: ['Test Automation', 'Selenium', 'Agile Testing'], photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400', isAvailable: true },
  { id: 10, name: 'James Anderson', email: 'james.anderson@company.co.uk', role: 'DevOps Engineer', department: 'Technology', costRate: 58, billRate: 135, skills: ['Kubernetes', 'CI/CD', 'Terraform'], photo: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400', isAvailable: true },
  { id: 11, name: 'Katie White', email: 'katie.white@company.co.uk', role: 'Consultant', department: 'Consulting', costRate: 52, billRate: 120, skills: ['Business Analysis', 'Workshops', 'Documentation'], photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400', isAvailable: true },
  { id: 12, name: 'Liam Harris', email: 'liam.harris@company.co.uk', role: 'Developer', department: 'Technology', costRate: 45, billRate: 105, skills: ['JavaScript', 'React', 'Node.js'], photo: 'https://images.unsplash.com/photo-1463453091185-61582044d556?w=400', isAvailable: true },
  { id: 13, name: 'Mia Thompson', email: 'mia.thompson@company.co.uk', role: 'Project Coordinator', department: 'Delivery', costRate: 35, billRate: 80, skills: ['Scheduling', 'Documentation', 'Stakeholder Communication'], photo: 'https://images.unsplash.com/photo-1487412720507-e7ab37f1c39e?w=400', isAvailable: true },
  { id: 14, name: 'Noah Martin', email: 'noah.martin@company.co.uk', role: 'Solutions Architect', department: 'Technology', costRate: 75, billRate: 170, skills: ['Architecture', 'Cloud', 'Security'], photo: 'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=400', isAvailable: true },
  { id: 15, name: 'Olivia Jackson', email: 'olivia.jackson@company.co.uk', role: 'Change Manager', department: 'Consulting', costRate: 60, billRate: 140, skills: ['Change Management', 'Training', 'Communications'], photo: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400', isAvailable: true },
  { id: 16, name: 'Peter Clarke', email: 'peter.clarke@company.co.uk', role: 'Technical Lead', department: 'Technology', costRate: 65, billRate: 150, skills: ['.NET', 'Azure', 'Microservices'], photo: 'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=400', isAvailable: true },
  { id: 17, name: 'Quinn Moore', email: 'quinn.moore@company.co.uk', role: 'Scrum Master', department: 'Delivery', costRate: 50, billRate: 115, skills: ['Scrum', 'Agile Coaching', 'Facilitation'], photo: 'https://images.unsplash.com/photo-1488161628813-04466f872be2?w=400', isAvailable: true },
  { id: 18, name: 'Rachel King', email: 'rachel.king@company.co.uk', role: 'Financial Analyst', department: 'Finance', costRate: 48, billRate: 110, skills: ['Financial Modelling', 'Excel', 'Reporting'], photo: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400', isAvailable: true },
  { id: 19, name: 'Samuel Wright', email: 'samuel.wright@company.co.uk', role: 'Security Specialist', department: 'Technology', costRate: 70, billRate: 160, skills: ['Cybersecurity', 'Penetration Testing', 'Compliance'], photo: 'https://images.unsplash.com/photo-1506919258185-6078bba55d2a?w=400', isAvailable: true },
  { id: 20, name: 'Tara Scott', email: 'tara.scott@company.co.uk', role: 'Product Owner', department: 'Delivery', costRate: 60, billRate: 140, skills: ['Product Management', 'Roadmapping', 'Prioritisation'], photo: 'https://images.unsplash.com/photo-1502685104226-ee32379fefbe?w=400', isAvailable: true },
  // Additional team members for better role coverage
  { id: 21, name: 'Marcus Chen', email: 'marcus.chen@company.co.uk', role: 'Senior Developer', department: 'Technology', costRate: 56, billRate: 132, skills: ['React', 'TypeScript', 'Node.js', 'AWS'], isAvailable: true },
  { id: 22, name: 'Sophia Patel', email: 'sophia.patel@company.co.uk', role: 'Senior Developer', department: 'Technology', costRate: 57, billRate: 133, skills: ['Angular', 'Java', 'Spring Boot', 'Docker'], isAvailable: true },
  { id: 23, name: 'Oliver Hughes', email: 'oliver.hughes@company.co.uk', role: 'Senior Developer', department: 'Technology', costRate: 55, billRate: 130, skills: ['Vue.js', 'Python', 'Django', 'PostgreSQL'], isAvailable: true },
  { id: 24, name: 'Amelia Foster', email: 'amelia.foster@company.co.uk', role: 'QA Analyst', department: 'Quality', costRate: 45, billRate: 105, skills: ['Manual Testing', 'Test Cases', 'Bug Tracking', 'Agile'], isAvailable: true },
  { id: 25, name: 'Lucas Bennett', email: 'lucas.bennett@company.co.uk', role: 'QA Analyst', department: 'Quality', costRate: 46, billRate: 107, skills: ['Automated Testing', 'Selenium', 'Cypress', 'API Testing'], isAvailable: true },
  { id: 26, name: 'Charlotte Reed', email: 'charlotte.reed@company.co.uk', role: 'QA Analyst', department: 'Quality', costRate: 44, billRate: 103, skills: ['Performance Testing', 'JMeter', 'Load Testing', 'Quality Assurance'], isAvailable: true },
  { id: 27, name: 'Ethan Murphy', email: 'ethan.murphy@company.co.uk', role: 'UX Designer', department: 'Design', costRate: 49, billRate: 112, skills: ['UI Design', 'Adobe XD', 'Wireframing', 'User Testing'], isAvailable: true },
  { id: 28, name: 'Ava Richardson', email: 'ava.richardson@company.co.uk', role: 'UX Designer', department: 'Design', costRate: 47, billRate: 109, skills: ['Sketch', 'InVision', 'Design Systems', 'Accessibility'], isAvailable: true },
  { id: 29, name: 'William Cooper', email: 'william.cooper@company.co.uk', role: 'Business Analyst', department: 'Consulting', costRate: 51, billRate: 117, skills: ['Stakeholder Analysis', 'Use Cases', 'BPMN', 'Agile'], isAvailable: true },
  { id: 30, name: 'Emily Watson', email: 'emily.watson@company.co.uk', role: 'Business Analyst', department: 'Consulting', costRate: 49, billRate: 113, skills: ['Data Analysis', 'Requirements', 'Documentation', 'Jira'], isAvailable: true },
  { id: 31, name: 'Daniel Brooks', email: 'daniel.brooks@company.co.uk', role: 'DevOps Engineer', department: 'Technology', costRate: 59, billRate: 137, skills: ['Jenkins', 'Docker', 'Kubernetes', 'AWS'], isAvailable: true },
  { id: 32, name: 'Isabella Gray', email: 'isabella.gray@company.co.uk', role: 'DevOps Engineer', department: 'Technology', costRate: 57, billRate: 134, skills: ['Azure DevOps', 'Terraform', 'Ansible', 'Monitoring'], isAvailable: true },
  { id: 33, name: 'Jacob Powell', email: 'jacob.powell@company.co.uk', role: 'Project Manager', department: 'Delivery', costRate: 54, billRate: 123, skills: ['Waterfall', 'MS Project', 'Budget Management', 'Reporting'], isAvailable: true },
  { id: 34, name: 'Megan Sanders', email: 'megan.sanders@company.co.uk', role: 'Project Manager', department: 'Delivery', costRate: 56, billRate: 127, skills: ['Hybrid PM', 'Risk Management', 'Vendor Management', 'PMO'], isAvailable: true },
  { id: 35, name: 'Ryan Mitchell', email: 'ryan.mitchell@company.co.uk', role: 'Data Analyst', department: 'Analytics', costRate: 46, billRate: 107, skills: ['Tableau', 'R', 'Statistics', 'Data Visualization'], isAvailable: true },
];

// Generate realistic projects
export const mockProjects: Project[] = [
  // Green - On Track Projects
  {
    id: 1,
    name: 'Digital Transformation Programme',
    code: 'DLT-DTP-001',
    completionPercentMode: 'based-on-time' as const,
    clientIds: '1', clientNames: 'Deloitte UK',
    projectManagerId: 1,
    type: 'time-and-materials',
    statusId: 2,
    health: 'green',
    startDate: '2024-01-15',
    endDate: '2025-06-30',
    description: 'Enterprise-wide digital transformation including cloud migration and process automation',
    budgetLabour: 850000,
    budgetExpense: 150000,
    budgetRevenue: 1200000,
    actualLabour: 425000,
    actualExpense: 68000,
    actualRevenue: 600000,
    budgetHours: 13000,
    actualHours: 6500,
    hoursVariance: -100,
    hoursVariancePercent: -1.5,
    totalBudget: 1000000,
    totalActual: 493000,
    variance: -7000,
    variancePercent: -1.4,
    marginPercent: 18.5,
    completionPercent: 48,
    revenueRecognitionMethod: 'percentage-of-completion',
    wipAmount: 93000,
    unbilledAmount: 45000,
    createdAt: '2023-12-01T10:00:00Z',
    updatedAt: '2025-01-15T14:30:00Z',
  },
  {
    id: 2,
    name: 'Financial Systems Integration',
    code: 'KPMG-FSI-002',
    // Phase 14.3 — PM override demo story:
    // Auto-calculated % from approved hours (3 200 ÷ 5 800 ≈ 55%) sat below the 75%
    // billing threshold. The PM reviewed the delivery status and set a manual override
    // of 78% — tipping the "75% Complete" milestone from Scheduled → Due on the
    // Billing Tab and making the project Ready to Bill on WIP Reporting.
    // The audit trail (mockCompletionAuditData[2]) records this change.
    completionPercentMode: 'manual' as const,
    completionPercentOverride: 78,
    completionPercentOverriddenBy: 'Sarah Chen',
    completionPercentOverriddenAt: '2025-07-10T11:30:00Z',
    billingTriggerType: 'percent-complete' as const,
    clientIds: '2', clientNames: 'KPMG London',
    projectManagerId: 2,
    type: 'fixed-price',
    statusId: 2,
    health: 'green',
    startDate: '2024-09-01',
    endDate: '2025-03-31',
    description: 'Integration of Access Financials with existing ERP systems',
    budgetLabour: 320000,
    budgetExpense: 30000,
    budgetRevenue: 450000,
    actualLabour: 180000,
    actualExpense: 15000,
    actualRevenue: 225000,
    budgetHours: 5800,
    actualHours: 3200,
    hoursVariance: -80,
    hoursVariancePercent: -1.4,
    totalBudget: 350000,
    totalActual: 195000,
    variance: -5000,
    variancePercent: -1.4,
    marginPercent: 28.3,
    completionPercent: 55,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 30000,
    unbilledAmount: 0,
    createdAt: '2024-08-01T09:00:00Z',
    updatedAt: '2025-07-10T11:30:00Z',
  },
  {
    id: 3,
    name: 'Infrastructure Modernisation',
    code: 'ARUP-INF-003',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'date' as const,
    clientIds: '3', clientNames: 'Arup Group',
    projectManagerId: 1,
    type: 'fixed-price',
    statusId: 2,
    health: 'green',
    startDate: '2024-06-01',
    endDate: '2025-05-31',
    description: 'Cloud infrastructure setup and migration to Azure',
    budgetLabour: 580000,
    budgetExpense: 120000,
    budgetRevenue: 900000,
    actualLabour: 310000,
    actualExpense: 58000,
    actualRevenue: 450000,
    budgetHours: 10000,
    actualHours: 5200,
    hoursVariance: -260,
    hoursVariancePercent: -2.6,
    totalBudget: 700000,
    totalActual: 368000,
    variance: -18000,
    variancePercent: -2.6,
    marginPercent: 27.8,
    completionPercent: 52,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 82000,
    unbilledAmount: 0,
    createdAt: '2024-05-01T11:00:00Z',
    updatedAt: '2025-01-15T10:15:00Z',
  },
  
  // Amber - At Risk Projects
  {
    id: 4,
    name: 'Legal Case Management System',
    code: 'CLIF-LCMS-004',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'percent-complete' as const,
    clientIds: '4', clientNames: 'Clifford Chance',
    projectManagerId: 2,
    type: 'fixed-price',
    statusId: 2,
    health: 'amber',
    startDate: '2024-10-01',
    endDate: '2025-04-30',
    description: 'Custom case management platform for legal practice',
    budgetLabour: 280000,
    budgetExpense: 20000,
    budgetRevenue: 380000,
    actualLabour: 195000,
    actualExpense: 18000,
    actualRevenue: 190000,
    budgetHours: 5100,
    actualHours: 3550,
    hoursVariance: 390,
    hoursVariancePercent: 7.7,
    totalBudget: 300000,
    totalActual: 213000,
    variance: 23000,
    variancePercent: 7.7,
    marginPercent: 15.8,
    completionPercent: 30,
    revenueRecognitionMethod: 'percentage-of-completion',
    wipAmount: 23000,
    unbilledAmount: 23000,
    createdAt: '2024-09-01T10:00:00Z',
    updatedAt: '2025-01-15T13:45:00Z',
  },
  {
    id: 5,
    name: 'Mobile App Development',
    code: 'ACC-MAD-005',
    completionPercentMode: 'based-on-time' as const,
    clientIds: '5', clientNames: 'Accenture UK',
    projectManagerId: 20,
    type: 'time-and-materials',
    statusId: 2,
    health: 'amber',
    startDate: '2024-08-15',
    endDate: '2025-02-28',
    description: 'iOS and Android mobile application for field services',
    budgetLabour: 420000,
    budgetExpense: 30000,
    budgetRevenue: 550000,
    actualLabour: 310000,
    actualExpense: 28000,
    actualRevenue: 385000,
    budgetHours: 7600,
    actualHours: 5640,
    hoursVariance: 640,
    hoursVariancePercent: 8.4,
    totalBudget: 450000,
    totalActual: 338000,
    variance: 38000,
    variancePercent: 8.4,
    marginPercent: 16.5,
    completionPercent: 72,
    revenueRecognitionMethod: 'time-based',
    wipAmount: -47000,
    unbilledAmount: 0,
    createdAt: '2024-07-15T09:30:00Z',
    updatedAt: '2025-01-15T11:20:00Z',
  },
  {
    id: 6,
    name: 'Architectural Design Platform',
    code: 'FOS-ADP-006',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'percent-complete' as const,
    clientIds: '6', clientNames: 'Foster + Partners',
    projectManagerId: 3,
    type: 'fixed-price',
    statusId: 2,
    health: 'amber',
    startDate: '2024-07-01',
    endDate: '2025-06-30',
    description: 'Collaborative design platform with 3D visualization',
    budgetLabour: 680000,
    budgetExpense: 70000,
    budgetRevenue: 950000,
    actualLabour: 425000,
    actualExpense: 52000,
    actualRevenue: 475000,
    budgetHours: 11700,
    actualHours: 7310,
    hoursVariance: 420,
    hoursVariancePercent: 3.6,
    totalBudget: 750000,
    totalActual: 477000,
    variance: 27000,
    variancePercent: 3.6,
    marginPercent: 21.3,
    completionPercent: 82,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 2000,
    unbilledAmount: 0,
    createdAt: '2024-06-01T10:00:00Z',
    updatedAt: '2025-01-14T15:30:00Z',
  },
  
  // Red - Critical Projects
  {
    id: 7,
    name: 'ERP Implementation',
    code: 'PWC-ERP-007',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'date' as const,
    clientIds: '7', clientNames: 'PwC Birmingham',
    projectManagerId: 1,
    type: 'fixed-price',
    statusId: 2,
    health: 'red',
    startDate: '2024-05-01',
    endDate: '2025-01-31',
    description: 'Full ERP system implementation and data migration',
    budgetLabour: 520000,
    budgetExpense: 80000,
    budgetRevenue: 750000,
    actualLabour: 615000,
    actualExpense: 92000,
    actualRevenue: 525000,
    budgetHours: 9500,
    actualHours: 11190,
    hoursVariance: 1690,
    hoursVariancePercent: 17.8,
    totalBudget: 600000,
    totalActual: 707000,
    variance: 107000,
    variancePercent: 17.8,
    marginPercent: 5.7,
    completionPercent: 85,
    revenueRecognitionMethod: 'percentage-of-completion',
    wipAmount: 182000,
    unbilledAmount: 182000,
    createdAt: '2024-04-01T09:00:00Z',
    updatedAt: '2025-01-15T16:45:00Z',
  },
  {
    id: 8,
    name: 'Data Centre Migration',
    code: 'MOTT-DCM-008',
    completionPercentMode: 'based-on-time' as const,
    clientIds: '8', clientNames: 'Mott MacDonald',
    projectManagerId: 2,
    type: 'time-and-materials',
    statusId: 2,
    health: 'red',
    startDate: '2024-09-01',
    endDate: '2025-02-28',
    description: 'Physical data centre migration with zero downtime requirement',
    budgetLabour: 380000,
    budgetExpense: 120000,
    budgetRevenue: 650000,
    actualLabour: 445000,
    actualExpense: 138000,
    actualRevenue: 520000,
    budgetHours: 6900,
    actualHours: 8050,
    hoursVariance: 1150,
    hoursVariancePercent: 16.6,
    totalBudget: 500000,
    totalActual: 583000,
    variance: 83000,
    variancePercent: 16.6,
    marginPercent: 10.3,
    completionPercent: 78,
    revenueRecognitionMethod: 'time-based',
    wipAmount: 63000,
    unbilledAmount: 63000,
    createdAt: '2024-08-01T10:00:00Z',
    updatedAt: '2025-01-15T12:30:00Z',
  },
  
  // More varied projects
  {
    id: 9,
    name: 'Compliance Audit System',
    code: 'FRESH-CAS-009',
    completionPercentMode: 'based-on-time' as const,
    clientIds: '9', clientNames: 'Freshfields',
    projectManagerId: 3,
    type: 'retainer',
    statusId: 2,
    health: 'green',
    startDate: '2024-01-01',
    endDate: '2025-12-31',
    description: 'Ongoing compliance monitoring and audit support',
    budgetLabour: 240000,
    budgetExpense: 10000,
    budgetRevenue: 300000,
    actualLabour: 120000,
    actualExpense: 5000,
    actualRevenue: 150000,
    budgetHours: 4350,
    actualHours: 2175,
    hoursVariance: 0,
    hoursVariancePercent: 0,
    totalBudget: 250000,
    totalActual: 125000,
    variance: 0,
    variancePercent: 0,
    marginPercent: 20.0,
    completionPercent: 50,
    revenueRecognitionMethod: 'time-based',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2023-12-01T09:00:00Z',
    updatedAt: '2025-01-15T09:00:00Z',
  },
  {
    id: 10,
    name: 'Cloud Security Assessment',
    code: 'CAP-CSA-010',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'percent-complete' as const,
    clientIds: '10', clientNames: 'Capgemini UK',
    projectManagerId: 2,
    type: 'fixed-price',
    statusId: 2,
    health: 'green',
    startDate: '2024-11-01',
    endDate: '2025-03-31',
    description: 'Comprehensive security audit and penetration testing',
    budgetLabour: 180000,
    budgetExpense: 20000,
    budgetRevenue: 250000,
    actualLabour: 95000,
    actualExpense: 8000,
    actualRevenue: 125000,
    budgetHours: 3200,
    actualHours: 1680,
    hoursVariance: -50,
    hoursVariancePercent: -1.5,
    totalBudget: 200000,
    totalActual: 103000,
    variance: -7000,
    variancePercent: -3.5,
    marginPercent: 25.2,
    completionPercent: 48,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 22000,
    unbilledAmount: 0,
    createdAt: '2024-10-01T10:00:00Z',
    updatedAt: '2025-01-14T14:15:00Z',
  },
  {
    id: 11,
    name: 'Engineering Simulation Platform',
    code: 'ATK-ESP-011',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'date' as const,
    clientIds: '11', clientNames: 'Atkins Global',
    projectManagerId: 1,
    type: 'fixed-price',
    statusId: 2,
    health: 'amber',
    startDate: '2024-04-01',
    endDate: '2025-09-30',
    description: 'High-performance computing platform for engineering simulations',
    budgetLabour: 920000,
    budgetExpense: 180000,
    budgetRevenue: 1400000,
    actualLabour: 580000,
    actualExpense: 115000,
    actualRevenue: 840000,
    budgetHours: 15800,
    actualHours: 9950,
    hoursVariance: 650,
    hoursVariancePercent: 4.1,
    totalBudget: 1100000,
    totalActual: 695000,
    variance: 45000,
    variancePercent: 4.1,
    marginPercent: 23.8,
    completionPercent: 60,
    revenueRecognitionMethod: 'milestone',
    wipAmount: -145000,
    unbilledAmount: 0,
    createdAt: '2024-03-01T09:00:00Z',
    updatedAt: '2025-01-15T15:20:00Z',
  },
  {
    id: 12,
    name: 'Tax Compliance Portal',
    code: 'EY-TCP-012',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'percent-complete' as const,
    clientIds: '12', clientNames: 'EY Manchester',
    projectManagerId: 20,
    type: 'fixed-price',
    statusId: 2,
    health: 'green',
    startDate: '2024-10-15',
    endDate: '2025-04-15',
    description: 'Self-service tax compliance portal with MTD integration',
    budgetLabour: 260000,
    budgetExpense: 15000,
    budgetRevenue: 350000,
    actualLabour: 145000,
    actualExpense: 8000,
    actualRevenue: 175000,
    budgetHours: 4700,
    actualHours: 2630,
    hoursVariance: -120,
    hoursVariancePercent: -2.5,
    totalBudget: 275000,
    totalActual: 153000,
    variance: -7000,
    variancePercent: -2.5,
    marginPercent: 26.3,
    completionPercent: 52,
    revenueRecognitionMethod: 'percentage-of-completion',
    wipAmount: 22000,
    unbilledAmount: 0,
    createdAt: '2024-09-15T10:00:00Z',
    updatedAt: '2025-01-15T11:45:00Z',
  },
  {
    id: 13,
    name: 'Smart Building IoT System',
    code: 'ZAHA-IOT-013',
    completionPercentMode: 'based-on-time' as const,
    clientIds: '13', clientNames: 'Zaha Hadid Architects',
    projectManagerId: 3,
    type: 'time-and-materials',
    statusId: 2,
    health: 'green',
    startDate: '2024-08-01',
    endDate: '2025-07-31',
    description: 'IoT sensor network and building management system',
    budgetLabour: 480000,
    budgetExpense: 220000,
    budgetRevenue: 900000,
    actualLabour: 240000,
    actualExpense: 105000,
    actualRevenue: 450000,
    budgetHours: 8200,
    actualHours: 4120,
    hoursVariance: -60,
    hoursVariancePercent: -0.7,
    totalBudget: 700000,
    totalActual: 345000,
    variance: -5000,
    variancePercent: -0.7,
    marginPercent: 28.3,
    completionPercent: 49,
    revenueRecognitionMethod: 'time-based',
    wipAmount: 105000,
    unbilledAmount: 55000,
    createdAt: '2024-07-01T09:00:00Z',
    updatedAt: '2025-01-15T10:30:00Z',
  },
  {
    id: 14,
    name: 'Financial Reporting Automation',
    code: 'BDO-FRA-014',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'percent-complete' as const,
    clientIds: '14', clientNames: 'BDO London',
    projectManagerId: 2,
    type: 'fixed-price',
    statusId: 2,
    health: 'amber',
    startDate: '2024-09-15',
    endDate: '2025-03-15',
    description: 'Automated financial reporting with Power BI dashboards',
    budgetLabour: 220000,
    budgetExpense: 30000,
    budgetRevenue: 320000,
    actualLabour: 165000,
    actualExpense: 25000,
    actualRevenue: 192000,
    budgetHours: 4000,
    actualHours: 3000,
    hoursVariance: 240,
    hoursVariancePercent: 6.0,
    totalBudget: 250000,
    totalActual: 190000,
    variance: 15000,
    variancePercent: 6.0,
    marginPercent: 18.8,
    completionPercent: 68,
    revenueRecognitionMethod: 'milestone',
    wipAmount: -2000,
    unbilledAmount: 0,
    createdAt: '2024-08-15T10:00:00Z',
    updatedAt: '2025-01-14T16:00:00Z',
  },
  {
    id: 15,
    name: 'Contract Management System',
    code: 'SLAU-CMS-015',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'date' as const,
    clientIds: '15', clientNames: 'Slaughter and May',
    projectManagerId: 1,
    type: 'fixed-price',
    statusId: 2,
    health: 'green',
    startDate: '2024-07-15',
    endDate: '2025-05-15',
    description: 'Enterprise contract lifecycle management platform',
    budgetLabour: 540000,
    budgetExpense: 60000,
    budgetRevenue: 780000,
    actualLabour: 310000,
    actualExpense: 32000,
    actualRevenue: 390000,
    budgetHours: 9800,
    actualHours: 5640,
    hoursVariance: -130,
    hoursVariancePercent: -1.3,
    totalBudget: 600000,
    totalActual: 342000,
    variance: -8000,
    variancePercent: -1.3,
    marginPercent: 25.6,
    completionPercent: 55,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 48000,
    unbilledAmount: 0,
    createdAt: '2024-06-15T09:00:00Z',
    updatedAt: '2025-01-15T14:00:00Z',
  },
  
  // Completed projects
  {
    id: 16,
    name: 'Website Redesign',
    code: 'DLT-WEB-016',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'percent-complete' as const,
    clientIds: '1', clientNames: 'Deloitte UK',
    projectManagerId: 20,
    type: 'fixed-price',
    statusId: 6,
    health: 'green',
    startDate: '2024-06-01',
    endDate: '2024-12-31',
    description: 'Corporate website redesign and CMS migration',
    budgetLabour: 180000,
    budgetExpense: 20000,
    budgetRevenue: 250000,
    actualLabour: 175000,
    actualExpense: 18000,
    actualRevenue: 250000,
    budgetHours: 3270,
    actualHours: 3180,
    hoursVariance: -115,
    hoursVariancePercent: -3.5,
    totalBudget: 200000,
    totalActual: 193000,
    variance: -7000,
    variancePercent: -3.5,
    marginPercent: 22.8,
    completionPercent: 100,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2024-05-01T09:00:00Z',
    updatedAt: '2024-12-31T17:00:00Z',
  },
  {
    id: 17,
    name: 'Cybersecurity Training Programme',
    code: 'ACC-CYB-017',
    completionPercentMode: 'based-on-time' as const,
    clientIds: '5', clientNames: 'Accenture UK',
    projectManagerId: 3,
    type: 'time-and-materials',
    statusId: 6,
    health: 'green',
    startDate: '2024-08-01',
    endDate: '2024-12-15',
    description: 'Organisation-wide security awareness training',
    budgetLabour: 95000,
    budgetExpense: 5000,
    budgetRevenue: 120000,
    actualLabour: 92000,
    actualExpense: 4500,
    actualRevenue: 120000,
    budgetHours: 1720,
    actualHours: 1670,
    hoursVariance: -50,
    hoursVariancePercent: -2.9,
    totalBudget: 100000,
    totalActual: 96500,
    variance: -3500,
    variancePercent: -3.5,
    marginPercent: 19.6,
    completionPercent: 100,
    revenueRecognitionMethod: 'time-based',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2024-07-01T09:00:00Z',
    updatedAt: '2024-12-15T17:00:00Z',
  },
  
  // Not started projects
  {
    id: 18,
    name: 'AI-Powered Analytics Platform',
    code: 'KPMG-AI-018',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'percent-complete' as const,
    clientIds: '2', clientNames: 'KPMG London',
    projectManagerId: 1,
    type: 'fixed-price',
    statusId: 1,
    health: 'green',
    startDate: '2025-02-01',
    endDate: '2025-11-30',
    description: 'Machine learning analytics platform for financial forecasting',
    budgetLabour: 780000,
    budgetExpense: 120000,
    budgetRevenue: 1200000,
    actualLabour: 0,
    actualExpense: 0,
    actualRevenue: 0,
    budgetHours: 14200,
    actualHours: 0,
    hoursVariance: 0,
    hoursVariancePercent: 0,
    totalBudget: 900000,
    totalActual: 0,
    variance: 0,
    variancePercent: 0,
    marginPercent: 33.3,
    completionPercent: 0,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2025-01-10T10:00:00Z',
    updatedAt: '2025-01-10T10:00:00Z',
  },
  {
    id: 19,
    name: 'Blockchain Supply Chain Tracker',
    code: 'ARUP-BLK-019',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'date' as const,
    clientIds: '3', clientNames: 'Arup Group',
    projectManagerId: 2,
    type: 'fixed-price',
    statusId: 1,
    health: 'amber',
    startDate: '2025-03-01',
    endDate: '2025-12-31',
    description: 'Blockchain-based supply chain tracking and verification system',
    budgetLabour: 650000,
    budgetExpense: 100000,
    budgetRevenue: 950000,
    actualLabour: 0,
    actualExpense: 0,
    actualRevenue: 0,
    budgetHours: 11800,
    actualHours: 0,
    hoursVariance: 0,
    hoursVariancePercent: 0,
    totalBudget: 750000,
    totalActual: 0,
    variance: 0,
    variancePercent: 0,
    marginPercent: 26.3,
    completionPercent: 0,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2025-01-12T09:00:00Z',
    updatedAt: '2025-01-12T09:00:00Z',
  },
  {
    id: 20,
    name: 'Legacy System Replacement',
    code: 'CLIF-LEG-020',
    completionPercentMode: 'based-on-time' as const,
    clientIds: '4', clientNames: 'Clifford Chance',
    projectManagerId: 3,
    type: 'time-and-materials',
    statusId: 1,
    health: 'red',
    startDate: '2025-04-01',
    endDate: '2026-03-31',
    description: 'Critical legacy system replacement with tight regulatory deadline',
    budgetLabour: 1200000,
    budgetExpense: 200000,
    budgetRevenue: 1800000,
    actualLabour: 0,
    actualExpense: 0,
    actualRevenue: 0,
    budgetHours: 21800,
    actualHours: 0,
    hoursVariance: 0,
    hoursVariancePercent: 0,
    totalBudget: 1400000,
    totalActual: 0,
    variance: 0,
    variancePercent: 0,
    marginPercent: 28.6,
    completionPercent: 0,
    revenueRecognitionMethod: 'percentage-of-completion',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2025-01-08T10:00:00Z',
    updatedAt: '2025-01-08T10:00:00Z',
  },

  // On Hold projects
  {
    id: 21,
    name: 'Customer Portal Upgrade',
    code: 'ACC-CPU-021',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'date' as const,
    clientIds: '5', clientNames: 'Accenture UK',
    projectManagerId: 1,
    type: 'fixed-price',
    statusId: 3,
    health: 'green',
    startDate: '2024-09-01',
    endDate: '2025-06-30',
    description: 'Customer self-service portal upgrade - paused for budget review',
    budgetLabour: 340000,
    budgetExpense: 40000,
    budgetRevenue: 480000,
    actualLabour: 170000,
    actualExpense: 18000,
    actualRevenue: 240000,
    budgetHours: 6200,
    actualHours: 3100,
    hoursVariance: -50,
    hoursVariancePercent: -0.8,
    totalBudget: 380000,
    totalActual: 188000,
    variance: -2000,
    variancePercent: -0.5,
    marginPercent: 21.7,
    completionPercent: 50,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 52000,
    unbilledAmount: 0,
    createdAt: '2024-08-01T09:00:00Z',
    updatedAt: '2025-01-05T14:00:00Z',
  },
  {
    id: 22,
    name: 'HR System Integration',
    code: 'FOS-HR-022',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'percent-complete' as const,
    clientIds: '6', clientNames: 'Foster + Partners',
    projectManagerId: 2,
    type: 'fixed-price',
    statusId: 3,
    health: 'amber',
    startDate: '2024-10-01',
    endDate: '2025-05-31',
    description: 'HR system integration paused due to vendor delays',
    budgetLabour: 280000,
    budgetExpense: 35000,
    budgetRevenue: 400000,
    actualLabour: 168000,
    actualExpense: 22000,
    actualRevenue: 200000,
    budgetHours: 5100,
    actualHours: 3060,
    hoursVariance: 180,
    hoursVariancePercent: 3.5,
    totalBudget: 315000,
    totalActual: 190000,
    variance: 11000,
    variancePercent: 3.5,
    marginPercent: 17.5,
    completionPercent: 55,
    revenueRecognitionMethod: 'milestone',
    wipAmount: -10000,
    unbilledAmount: 0,
    createdAt: '2024-09-01T10:00:00Z',
    updatedAt: '2025-01-03T11:30:00Z',
  },
  {
    id: 23,
    name: 'Regulatory Compliance Overhaul',
    code: 'PWC-REG-023',
    completionPercentMode: 'based-on-time' as const,
    clientIds: '7', clientNames: 'PwC Birmingham',
    projectManagerId: 3,
    type: 'time-and-materials',
    statusId: 3,
    health: 'red',
    startDate: '2024-07-01',
    endDate: '2025-04-30',
    description: 'Compliance system overhaul on hold pending regulatory clarification',
    budgetLabour: 520000,
    budgetExpense: 80000,
    budgetRevenue: 780000,
    actualLabour: 390000,
    actualExpense: 68000,
    actualRevenue: 468000,
    budgetHours: 9500,
    actualHours: 7125,
    hoursVariance: 950,
    hoursVariancePercent: 10.0,
    totalBudget: 600000,
    totalActual: 458000,
    variance: 58000,
    variancePercent: 9.7,
    marginPercent: 12.3,
    completionPercent: 65,
    revenueRecognitionMethod: 'time-based',
    wipAmount: -10000,
    unbilledAmount: 0,
    createdAt: '2024-06-01T09:00:00Z',
    updatedAt: '2025-01-02T16:45:00Z',
  },

  // Cancelled projects
  {
    id: 24,
    name: 'Mobile Banking App',
    code: 'MOTT-MBA-024',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'percent-complete' as const,
    clientIds: '8', clientNames: 'Mott MacDonald',
    projectManagerId: 1,
    type: 'fixed-price',
    statusId: 5,
    health: 'green',
    startDate: '2024-05-01',
    endDate: '2024-12-31',
    description: 'Mobile banking app cancelled due to strategic pivot',
    budgetLabour: 450000,
    budgetExpense: 50000,
    budgetRevenue: 650000,
    actualLabour: 112500,
    actualExpense: 12000,
    actualRevenue: 162500,
    budgetHours: 8200,
    actualHours: 2050,
    hoursVariance: -50,
    hoursVariancePercent: -0.6,
    totalBudget: 500000,
    totalActual: 124500,
    variance: -500,
    variancePercent: -0.1,
    marginPercent: 23.4,
    completionPercent: 25,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2024-04-01T09:00:00Z',
    updatedAt: '2024-08-15T10:00:00Z',
  },
  {
    id: 25,
    name: 'Warehouse Automation System',
    code: 'FRESH-WAS-025',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'date' as const,
    clientIds: '9', clientNames: 'Freshfields',
    projectManagerId: 2,
    type: 'fixed-price',
    statusId: 5,
    health: 'amber',
    startDate: '2024-06-01',
    endDate: '2025-03-31',
    description: 'Warehouse automation cancelled due to budget constraints',
    budgetLabour: 380000,
    budgetExpense: 120000,
    budgetRevenue: 650000,
    actualLabour: 152000,
    actualExpense: 45000,
    actualRevenue: 260000,
    budgetHours: 6900,
    actualHours: 2760,
    hoursVariance: 200,
    hoursVariancePercent: 2.9,
    totalBudget: 500000,
    totalActual: 197000,
    variance: 7000,
    variancePercent: 1.4,
    marginPercent: 19.2,
    completionPercent: 35,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2024-05-01T10:00:00Z',
    updatedAt: '2024-10-20T14:30:00Z',
  },
  {
    id: 26,
    name: 'Global CRM Rollout',
    code: 'CAP-CRM-026',
    completionPercentMode: 'based-on-time' as const,
    clientIds: '10', clientNames: 'Capgemini UK',
    projectManagerId: 3,
    type: 'time-and-materials',
    statusId: 5,
    health: 'red',
    startDate: '2024-03-01',
    endDate: '2024-11-30',
    description: 'CRM rollout cancelled due to vendor bankruptcy',
    budgetLabour: 680000,
    budgetExpense: 120000,
    budgetRevenue: 1000000,
    actualLabour: 340000,
    actualExpense: 85000,
    actualRevenue: 500000,
    budgetHours: 12400,
    actualHours: 6200,
    hoursVariance: 1200,
    hoursVariancePercent: 9.7,
    totalBudget: 800000,
    totalActual: 425000,
    variance: 75000,
    variancePercent: 9.4,
    marginPercent: 7.5,
    completionPercent: 45,
    revenueRecognitionMethod: 'time-based',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2024-02-01T09:00:00Z',
    updatedAt: '2024-09-01T11:00:00Z',
  },

  // Additional completed projects with different health statuses
  {
    id: 27,
    name: 'Data Migration Project',
    code: 'ATK-DMP-027',
    completionPercentMode: 'based-on-time' as const,
    billingTriggerType: 'date' as const,
    clientIds: '11', clientNames: 'Atkins Global',
    projectManagerId: 1,
    type: 'fixed-price',
    statusId: 6,
    health: 'amber',
    startDate: '2024-04-01',
    endDate: '2024-10-31',
    description: 'Legacy data migration completed with minor delays',
    budgetLabour: 220000,
    budgetExpense: 30000,
    budgetRevenue: 320000,
    actualLabour: 242000,
    actualExpense: 35000,
    actualRevenue: 320000,
    budgetHours: 4000,
    actualHours: 4400,
    hoursVariance: 400,
    hoursVariancePercent: 10.0,
    totalBudget: 250000,
    totalActual: 277000,
    variance: 27000,
    variancePercent: 10.8,
    marginPercent: 13.4,
    completionPercent: 100,
    revenueRecognitionMethod: 'milestone',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2024-03-01T09:00:00Z',
    updatedAt: '2024-10-31T17:00:00Z',
  },
  {
    id: 28,
    name: 'Emergency System Recovery',
    code: 'EY-ESR-028',
    completionPercentMode: 'based-on-time' as const,
    clientIds: '12', clientNames: 'EY Manchester',
    projectManagerId: 2,
    type: 'time-and-materials',
    statusId: 6,
    health: 'red',
    startDate: '2024-02-01',
    endDate: '2024-06-30',
    description: 'Emergency system recovery completed significantly over budget',
    budgetLabour: 180000,
    budgetExpense: 20000,
    budgetRevenue: 250000,
    actualLabour: 270000,
    actualExpense: 45000,
    actualRevenue: 250000,
    budgetHours: 3270,
    actualHours: 4905,
    hoursVariance: 1635,
    hoursVariancePercent: 50.0,
    totalBudget: 200000,
    totalActual: 315000,
    variance: 115000,
    variancePercent: 57.5,
    marginPercent: -26.0,
    completionPercent: 100,
    revenueRecognitionMethod: 'time-based',
    wipAmount: 0,
    unbilledAmount: 0,
    createdAt: '2024-01-15T09:00:00Z',
    updatedAt: '2024-06-30T17:00:00Z',
  },
];

// ============================================================================
// COMPLETION % AUDIT TRAIL — Phase 12.3
// Mock audit entries for even-ID Fixed Fee (% complete) projects.
// Keyed by project ID. Each array is sorted oldest → newest.
// ============================================================================
export const mockCompletionAuditData: Record<number, CompletionPercentAuditEntry[]> = {
  // Project 2 — Financial Systems Integration (KPMG-FSI-002) | completionPercent: 55
  2: [
    {
      id: 'audit-2-001',
      projectId: 2,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'auto-calculation',
      previousValue: 0,
      newValue: 22,
      changedAt: '2024-10-01T09:00:00Z',
      approvedHoursAtChange: 1276,
      budgetHoursAtChange: 5800,
    },
    {
      id: 'audit-2-002',
      projectId: 2,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'auto-calculation',
      previousValue: 22,
      newValue: 38,
      changedAt: '2024-11-01T09:00:00Z',
      approvedHoursAtChange: 2204,
      budgetHoursAtChange: 5800,
    },
    {
      id: 'audit-2-003',
      projectId: 2,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'manual-override',
      previousValue: 38,
      newValue: 45,
      changedAt: '2024-11-20T14:30:00Z',
    },
    {
      id: 'audit-2-004',
      projectId: 2,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'override-cleared',
      previousValue: 45,
      newValue: 48,
      changedAt: '2024-12-03T10:00:00Z',
      approvedHoursAtChange: 2784,
      budgetHoursAtChange: 5800,
    },
    {
      id: 'audit-2-005',
      projectId: 2,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'auto-calculation',
      previousValue: 48,
      newValue: 55,
      changedAt: '2025-01-07T09:00:00Z',
      approvedHoursAtChange: 3190,
      budgetHoursAtChange: 5800,
    },
    {
      // Phase 14.3 — PM override demo story.
      // The approved hours (3 200 ÷ 5 800) suggested 55%, which sat just below
      // the 75% billing threshold. Sarah reviewed delivery progress and set a
      // manual override of 78% — immediately tipping the "75% Complete" milestone
      // from Scheduled → Due on the Billing Tab.
      id: 'audit-2-006',
      projectId: 2,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'manual-override',
      previousValue: 55,
      newValue: 78,
      changedAt: '2025-07-10T11:30:00Z',
    },
  ],

  // Project 4 — Legal Case Management System (CLIF-LCMS-004) | completionPercent: 30
  4: [
    {
      id: 'audit-4-001',
      projectId: 4,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'auto-calculation',
      previousValue: 0,
      newValue: 12,
      changedAt: '2024-10-15T09:00:00Z',
      approvedHoursAtChange: 612,
      budgetHoursAtChange: 5100,
    },
    {
      id: 'audit-4-002',
      projectId: 4,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'auto-calculation',
      previousValue: 12,
      newValue: 24,
      changedAt: '2024-11-15T09:00:00Z',
      approvedHoursAtChange: 1224,
      budgetHoursAtChange: 5100,
    },
    {
      id: 'audit-4-003',
      projectId: 4,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'manual-override',
      previousValue: 24,
      newValue: 30,
      changedAt: '2024-12-10T11:15:00Z',
    },
  ],

  // Project 6 — Architectural Design Platform (FOS-ADP-006) | completionPercent: 82
  6: [
    {
      id: 'audit-6-001',
      projectId: 6,
      changedBy: 'user-pm-003',
      changedByName: 'James Thornton',
      changeType: 'auto-calculation',
      previousValue: 0,
      newValue: 18,
      changedAt: '2024-08-01T09:00:00Z',
      approvedHoursAtChange: 2106,
      budgetHoursAtChange: 11700,
    },
    {
      id: 'audit-6-002',
      projectId: 6,
      changedBy: 'user-pm-003',
      changedByName: 'James Thornton',
      changeType: 'auto-calculation',
      previousValue: 18,
      newValue: 42,
      changedAt: '2024-10-01T09:00:00Z',
      approvedHoursAtChange: 4914,
      budgetHoursAtChange: 11700,
    },
    {
      id: 'audit-6-003',
      projectId: 6,
      changedBy: 'user-pm-003',
      changedByName: 'James Thornton',
      changeType: 'auto-calculation',
      previousValue: 42,
      newValue: 62,
      changedAt: '2024-11-15T09:00:00Z',
      approvedHoursAtChange: 7254,
      budgetHoursAtChange: 11700,
    },
    {
      id: 'audit-6-004',
      projectId: 6,
      changedBy: 'user-pm-003',
      changedByName: 'James Thornton',
      changeType: 'manual-override',
      previousValue: 62,
      newValue: 75,
      changedAt: '2024-12-05T15:45:00Z',
    },
    {
      id: 'audit-6-005',
      projectId: 6,
      changedBy: 'user-pm-003',
      changedByName: 'James Thornton',
      changeType: 'auto-calculation',
      previousValue: 75,
      newValue: 82,
      changedAt: '2025-01-10T09:00:00Z',
      approvedHoursAtChange: 9594,
      budgetHoursAtChange: 11700,
    },
  ],

  // Project 10 — Cloud Security Assessment (CAP-CSA-010) | completionPercent: 48
  10: [
    {
      id: 'audit-10-001',
      projectId: 10,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'auto-calculation',
      previousValue: 0,
      newValue: 20,
      changedAt: '2024-11-15T09:00:00Z',
      approvedHoursAtChange: 640,
      budgetHoursAtChange: 3200,
    },
    {
      id: 'audit-10-002',
      projectId: 10,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'auto-calculation',
      previousValue: 20,
      newValue: 35,
      changedAt: '2024-12-15T09:00:00Z',
      approvedHoursAtChange: 1120,
      budgetHoursAtChange: 3200,
    },
    {
      id: 'audit-10-003',
      projectId: 10,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'auto-calculation',
      previousValue: 35,
      newValue: 48,
      changedAt: '2025-01-10T09:00:00Z',
      approvedHoursAtChange: 1536,
      budgetHoursAtChange: 3200,
    },
  ],

  // Project 12 — Tax Compliance Portal (EY-TCP-012) | completionPercent: 52
  12: [
    {
      id: 'audit-12-001',
      projectId: 12,
      changedBy: 'user-pm-020',
      changedByName: 'Rachel Kim',
      changeType: 'auto-calculation',
      previousValue: 0,
      newValue: 18,
      changedAt: '2024-11-01T09:00:00Z',
      approvedHoursAtChange: 846,
      budgetHoursAtChange: 4700,
    },
    {
      id: 'audit-12-002',
      projectId: 12,
      changedBy: 'user-pm-020',
      changedByName: 'Rachel Kim',
      changeType: 'auto-calculation',
      previousValue: 18,
      newValue: 34,
      changedAt: '2024-12-01T09:00:00Z',
      approvedHoursAtChange: 1598,
      budgetHoursAtChange: 4700,
    },
    {
      id: 'audit-12-003',
      projectId: 12,
      changedBy: 'user-pm-020',
      changedByName: 'Rachel Kim',
      changeType: 'manual-override',
      previousValue: 34,
      newValue: 40,
      changedAt: '2024-12-18T13:00:00Z',
    },
    {
      id: 'audit-12-004',
      projectId: 12,
      changedBy: 'user-pm-020',
      changedByName: 'Rachel Kim',
      changeType: 'override-cleared',
      previousValue: 40,
      newValue: 52,
      changedAt: '2025-01-08T09:00:00Z',
      approvedHoursAtChange: 2444,
      budgetHoursAtChange: 4700,
    },
  ],

  // Project 14 — Financial Reporting Automation (BDO-FRA-014) | completionPercent: 68
  14: [
    {
      id: 'audit-14-001',
      projectId: 14,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'auto-calculation',
      previousValue: 0,
      newValue: 22,
      changedAt: '2024-10-01T09:00:00Z',
      approvedHoursAtChange: 880,
      budgetHoursAtChange: 4000,
    },
    {
      id: 'audit-14-002',
      projectId: 14,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'auto-calculation',
      previousValue: 22,
      newValue: 45,
      changedAt: '2024-11-01T09:00:00Z',
      approvedHoursAtChange: 1800,
      budgetHoursAtChange: 4000,
    },
    {
      id: 'audit-14-003',
      projectId: 14,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'auto-calculation',
      previousValue: 45,
      newValue: 60,
      changedAt: '2024-12-01T09:00:00Z',
      approvedHoursAtChange: 2400,
      budgetHoursAtChange: 4000,
    },
    {
      id: 'audit-14-004',
      projectId: 14,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'manual-override',
      previousValue: 60,
      newValue: 68,
      changedAt: '2025-01-06T10:30:00Z',
    },
  ],

  // Project 16 — Website Redesign (DLT-WEB-016) | completionPercent: 100 (completed)
  16: [
    {
      id: 'audit-16-001',
      projectId: 16,
      changedBy: 'user-pm-020',
      changedByName: 'Rachel Kim',
      changeType: 'auto-calculation',
      previousValue: 0,
      newValue: 30,
      changedAt: '2024-07-01T09:00:00Z',
      approvedHoursAtChange: 981,
      budgetHoursAtChange: 3270,
    },
    {
      id: 'audit-16-002',
      projectId: 16,
      changedBy: 'user-pm-020',
      changedByName: 'Rachel Kim',
      changeType: 'auto-calculation',
      previousValue: 30,
      newValue: 65,
      changedAt: '2024-09-01T09:00:00Z',
      approvedHoursAtChange: 2126,
      budgetHoursAtChange: 3270,
    },
    {
      id: 'audit-16-003',
      projectId: 16,
      changedBy: 'user-pm-020',
      changedByName: 'Rachel Kim',
      changeType: 'auto-calculation',
      previousValue: 65,
      newValue: 88,
      changedAt: '2024-11-01T09:00:00Z',
      approvedHoursAtChange: 2878,
      budgetHoursAtChange: 3270,
    },
    {
      id: 'audit-16-004',
      projectId: 16,
      changedBy: 'user-pm-020',
      changedByName: 'Rachel Kim',
      changeType: 'auto-calculation',
      previousValue: 88,
      newValue: 100,
      changedAt: '2024-12-31T17:00:00Z',
      approvedHoursAtChange: 3270,
      budgetHoursAtChange: 3270,
    },
  ],

  // Project 18 — AI-Powered Analytics Platform (KPMG-AI-018) | completionPercent: 0 (not started)
  18: [
    {
      id: 'audit-18-001',
      projectId: 18,
      changedBy: 'user-pm-001',
      changedByName: 'Alex Morgan',
      changeType: 'auto-calculation',
      previousValue: 0,
      newValue: 0,
      changedAt: '2025-02-01T09:00:00Z',
      approvedHoursAtChange: 0,
      budgetHoursAtChange: 14200,
    },
  ],

  // Project 22 — HR System Integration (FOS-HR-022) | completionPercent: 55 (on hold)
  22: [
    {
      id: 'audit-22-001',
      projectId: 22,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'auto-calculation',
      previousValue: 0,
      newValue: 28,
      changedAt: '2024-10-15T09:00:00Z',
      approvedHoursAtChange: 1428,
      budgetHoursAtChange: 5100,
    },
    {
      id: 'audit-22-002',
      projectId: 22,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'auto-calculation',
      previousValue: 28,
      newValue: 45,
      changedAt: '2024-11-15T09:00:00Z',
      approvedHoursAtChange: 2295,
      budgetHoursAtChange: 5100,
    },
    {
      id: 'audit-22-003',
      projectId: 22,
      changedBy: 'user-pm-002',
      changedByName: 'Sarah Chen',
      changeType: 'manual-override',
      previousValue: 45,
      newValue: 55,
      changedAt: '2025-01-03T11:30:00Z',
    },
  ],

  // Project 24 — Mobile Banking App (MOTT-MBA-024) | completionPercent: 25 (cancelled)
  24: [
    {
      id: 'audit-24-001',
      projectId: 24,
      changedBy: 'user-pm-001',
      changedByName: 'Alex Morgan',
      changeType: 'auto-calculation',
      previousValue: 0,
      newValue: 12,
      changedAt: '2024-05-15T09:00:00Z',
      approvedHoursAtChange: 984,
      budgetHoursAtChange: 8200,
    },
    {
      id: 'audit-24-002',
      projectId: 24,
      changedBy: 'user-pm-001',
      changedByName: 'Alex Morgan',
      changeType: 'auto-calculation',
      previousValue: 12,
      newValue: 25,
      changedAt: '2024-07-01T09:00:00Z',
      approvedHoursAtChange: 2050,
      budgetHoursAtChange: 8200,
    },
    {
      id: 'audit-24-003',
      projectId: 24,
      changedBy: 'user-pm-001',
      changedByName: 'Alex Morgan',
      changeType: 'auto-calculation',
      previousValue: 25,
      newValue: 25,
      changedAt: '2024-08-15T10:00:00Z',
      approvedHoursAtChange: 2050,
      budgetHoursAtChange: 8200,
    },
  ],
};

// Helper function to get project by ID
export const getProjectById = (id: number): Project | undefined => {
  return mockProjects.find(p => p.id === id);
};

// Helper function to get projects by client (now supports multiple clients per project)
export const getProjectsByClient = (clientId: number): Project[] => {
  return mockProjects.filter(p => p.clientIds?.split(',').includes(String(clientId)));
};

// Helper function to get projects by PM
export const getProjectsByPM = (pmId: number): Project[] => {
  return mockProjects.filter(p => p.projectManagerId === pmId);
};

// Helper function to get client by ID
export const getClientById = (id: number): Client | undefined => {
  return mockClients.find(c => c.id === id);
};

// Helper function to get employee by ID
export const getEmployeeById = (id: number): Employee | undefined => {
  return mockEmployees.find(e => e.id === id);
};

// Helper function to get allocations by project
export const getAllocationsByProject = (projectId: number): ResourceAllocation[] => {
  return mockResourceAllocations.filter(ra => ra.projectId === projectId);
};

// Resource Allocations
export const mockResourceAllocations: ResourceAllocation[] = [
  // Project 1 - Digital Transformation Programme
  { id: 1, projectId: 1, employeeId: 1, allocationPercent: 80, startDate: '2024-01-15', endDate: '2025-06-30', role: 'Project Manager', status: 'confirmed', conflictLevel: 'none' },
  { id: 2, projectId: 1, employeeId: 4, allocationPercent: 100, startDate: '2024-01-15', endDate: '2025-06-30', role: 'Lead Developer', status: 'confirmed', conflictLevel: 'none' },
  { id: 3, projectId: 1, employeeId: 5, allocationPercent: 100, startDate: '2024-01-15', endDate: '2025-06-30', role: 'Senior Developer', status: 'confirmed', conflictLevel: 'none' },
  { id: 4, projectId: 1, employeeId: 3, allocationPercent: 60, startDate: '2024-01-15', endDate: '2025-03-31', role: 'Senior Consultant', status: 'confirmed', conflictLevel: 'none' },
  { id: 5, projectId: 1, employeeId: 10, allocationPercent: 50, startDate: '2024-02-01', endDate: '2025-06-30', role: 'DevOps Engineer', status: 'confirmed', conflictLevel: 'none' },
  
  // Project 2 - Financial Systems Integration
  { id: 6, projectId: 2, employeeId: 2, allocationPercent: 70, startDate: '2024-09-01', endDate: '2025-03-31', role: 'Project Manager', status: 'confirmed', conflictLevel: 'none' },
  { id: 7, projectId: 2, employeeId: 6, allocationPercent: 100, startDate: '2024-09-01', endDate: '2025-03-31', role: 'Business Analyst', status: 'confirmed', conflictLevel: 'none' },
  { id: 8, projectId: 2, employeeId: 12, allocationPercent: 100, startDate: '2024-09-01', endDate: '2025-03-31', role: 'Developer', status: 'confirmed', conflictLevel: 'none' },
  { id: 9, projectId: 2, employeeId: 18, allocationPercent: 50, startDate: '2024-09-01', endDate: '2025-03-31', role: 'Financial Analyst', status: 'confirmed', conflictLevel: 'none' },
  
  // Project 3 - Infrastructure Modernisation
  { id: 10, projectId: 3, employeeId: 1, allocationPercent: 20, startDate: '2024-06-01', endDate: '2025-05-31', role: 'Senior Project Manager', status: 'confirmed', conflictLevel: 'warning' },
  { id: 11, projectId: 3, employeeId: 14, allocationPercent: 80, startDate: '2024-06-01', endDate: '2025-05-31', role: 'Solutions Architect', status: 'confirmed', conflictLevel: 'none' },
  { id: 12, projectId: 3, employeeId: 10, allocationPercent: 50, startDate: '2024-06-01', endDate: '2025-05-31', role: 'DevOps Engineer', status: 'confirmed', conflictLevel: 'critical' },
  { id: 13, projectId: 3, employeeId: 19, allocationPercent: 60, startDate: '2024-06-01', endDate: '2025-05-31', role: 'Security Specialist', status: 'confirmed', conflictLevel: 'none' },
  
  // Project 4 - Legal Case Management System
  { id: 14, projectId: 4, employeeId: 2, allocationPercent: 30, startDate: '2024-10-01', endDate: '2025-04-30', role: 'Project Manager', status: 'confirmed', conflictLevel: 'warning' },
  { id: 15, projectId: 4, employeeId: 11, allocationPercent: 100, startDate: '2024-10-01', endDate: '2025-04-30', role: 'Consultant', status: 'confirmed', conflictLevel: 'none' },
  { id: 16, projectId: 4, employeeId: 12, allocationPercent: 80, startDate: '2024-10-01', endDate: '2025-04-30', role: 'Developer', status: 'tentative', conflictLevel: 'warning' },
  { id: 17, projectId: 4, employeeId: 7, allocationPercent: 40, startDate: '2024-10-01', endDate: '2025-02-28', role: 'UX Designer', status: 'confirmed', conflictLevel: 'none' },
  
  // Project 5 - Mobile App Development
  { id: 18, projectId: 5, employeeId: 20, allocationPercent: 60, startDate: '2024-08-15', endDate: '2025-02-28', role: 'Product Owner', status: 'confirmed', conflictLevel: 'none' },
  { id: 19, projectId: 5, employeeId: 4, allocationPercent: 100, startDate: '2024-08-15', endDate: '2025-02-28', role: 'Lead Developer', status: 'tentative', conflictLevel: 'critical' },
  { id: 20, projectId: 5, employeeId: 5, allocationPercent: 100, startDate: '2024-08-15', endDate: '2025-02-28', role: 'Senior Developer', status: 'tentative', conflictLevel: 'critical' },
  { id: 21, projectId: 5, employeeId: 7, allocationPercent: 60, startDate: '2024-08-15', endDate: '2025-02-28', role: 'UX Designer', status: 'confirmed', conflictLevel: 'warning' },
  { id: 22, projectId: 5, employeeId: 9, allocationPercent: 50, startDate: '2024-08-15', endDate: '2025-02-28', role: 'QA Lead', status: 'confirmed', conflictLevel: 'none' },
];

// WIP Reports
export const mockWIPReports: WIPReport[] = mockProjects.map((project, index) => {
  // Shared base figures
  const costsIncurred = project.totalActual;
  const amountBilled = project.actualRevenue - project.unbilledAmount;

  // Derive project type
  const projectType: WIPReport['projectType'] =
    project.type === 'fixed-price' ? 'fixed-price'
    : project.type === 'retainer' ? 'retainer'
    : 'time-and-materials';

  // ─────────────────────────────────────────────────────────────────────────────
  // Phase 6 — Fixed Fee WIP Calculations
  // ─────────────────────────────────────────────────────────────────────────────

  // 6.1 — WIP Amount
  // T&M:         hours × rate, not yet invoiced  (project.wipAmount — unchanged)
  // Fixed Fee:   (% complete × contract value) − invoiced to date
  //
  // Phase 14.2 — use the effective completion % (PM override wins, else raw value).
  // This mirrors the logic in project-details.tsx (effectiveCompletionPercent) so
  // that WIP Reporting reacts to PM overrides in the same way the Billing Tab does.
  const effectiveCompletionPercent: number =
    project.completionPercentOverride !== undefined
      ? project.completionPercentOverride
      : project.completionPercent;

  const contractValue = project.budgetRevenue; // Sales Budget contract value
  const earnedRevenue =
    projectType === 'fixed-price'
      ? (effectiveCompletionPercent / 100) * contractValue
      : 0; // unused for T&M / retainer

  const wipAmount =
    projectType === 'fixed-price'
      ? earnedRevenue - amountBilled
      : project.wipAmount;

  const profitEarned = project.actualRevenue - costsIncurred;

  // ─────────────────────────────────────────────────────────────────────────────
  // Phase 6 / Phase 10 — Billing Event Generation (Fixed Fee)
  //
  // Phase 10 update: use actual billing milestones from budget mock data so that
  // % complete milestones are correctly represented in "Next Billing Event" and
  // "Ready to Bill" calculations.
  //
  // Priority order for "next" event:
  //   1. Overdue (due/overdue % complete or past-date milestones not yet invoiced)
  //   2. Due (% complete threshold met, or date due within 7 days)
  //   3. Upcoming % complete milestone with the lowest threshold not yet met
  //   4. Upcoming date milestone (soonest future date)
  // ─────────────────────────────────────────────────────────────────────────────
  let nextBillingEvent: string | undefined;
  let nextBillingEventAmount: number | undefined;
  let nextBillingEventTriggerType: 'date' | 'percent-complete' | undefined;
  let nextBillingEventTriggerPercent: number | undefined;
  // Phase 10.1: tracks whether any uninvoiced milestone is Due or Overdue for readyToBill.
  // Set inside the fixed-price block and consumed in the readyToBill calculation below.
  let hasActionableFixedFeeMilestone = false;

  if (project.statusId === 2) { // In Progress
    if (projectType === 'fixed-price') {
      // Pull actual milestones from budget mock data
      const budgetData = getBudgetDataByProjectId(project.id);
      const milestones = budgetData?.salesBudget?.milestones ?? [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // Phase 14.2 — use effective completion % (PM override wins, else raw value)
      const completionPct = effectiveCompletionPercent; // 0–100

      // Determine status of each milestone (mirroring the Billing Tab logic)
      type MilestoneWithStatus = {
        milestone: BillingMilestone;
        isActionable: boolean; // Due or Overdue — ready to invoice
        isOverdue: boolean;
        isScheduled: boolean;
        /** Effective trigger type for this milestone (date if no triggerPercent, even on % complete projects) */
        effectiveTriggerType: 'date' | 'percent-complete';
      };

      const annotated: MilestoneWithStatus[] = milestones
        // Phase 10.1: skip milestones that are already invoiced or paid — they
        // must not surface as "next billing event" or contribute to "ready to bill".
        .filter((m: BillingMilestone) => m.status !== 'paid' && m.status !== 'invoiced')
        .map((m: BillingMilestone) => {
          let isActionable = false;
          let isOverdue = false;
          let isScheduled = false;

          // Use the project-level billingTriggerType — not the (removed) per-milestone triggerType.
          // Exception: a milestone with no triggerPercent is always evaluated as date-based
          // (e.g. the deposit milestone on even-id percent-complete projects).
          const projectTriggerType = project.billingTriggerType ?? 'date';
          const effectiveTriggerType: 'date' | 'percent-complete' =
            projectTriggerType === 'percent-complete' && m.triggerPercent != null
              ? 'percent-complete'
              : 'date';

          if (effectiveTriggerType === 'percent-complete') {
            // Phase 10.1: check completionPercent >= triggerPercent using project.billingTriggerType
            const thresholdMet = completionPct >= (m.triggerPercent ?? 0);
            const deadlinePassed = m.dueDate ? new Date(m.dueDate) < today : false;
            if (thresholdMet || deadlinePassed) {
              isActionable = true;
              isOverdue = deadlinePassed;
            } else {
              isScheduled = true;
            }
          } else {
            // date-based
            if (!m.dueDate) { isScheduled = true; }
            else {
              const dueDate = new Date(m.dueDate);
              if (dueDate < today) {
                isActionable = true;
                isOverdue = true;
              } else {
                const sevenDaysAhead = new Date(today);
                sevenDaysAhead.setDate(today.getDate() + 7);
                isActionable = dueDate <= sevenDaysAhead;
                isScheduled = !isActionable;
              }
            }
          }

          return { milestone: m, isActionable, isOverdue, isScheduled, effectiveTriggerType };
        });

      // Phase 10.1: set the flag used later for readyToBill — true if any
      // uninvoiced milestone is currently Due or Overdue.
      hasActionableFixedFeeMilestone = annotated.some(a => a.isActionable);

      // Pick the most actionable milestone as "next billing event":
      // 1. Overdue items first
      // 2. Due items (% complete threshold met, or due within 7 days)
      // 3. Upcoming % complete (lowest threshold not yet met)
      // 4. Upcoming date milestone (soonest)
      const overdueItems = annotated.filter(a => a.isOverdue);
      const dueItems = annotated.filter(a => a.isActionable && !a.isOverdue);
      // Route scheduled milestones by their effectiveTriggerType (not just project-level type)
      // so a date-based deposit on a % complete project is still sorted by date.
      const scheduledPct = annotated.filter(a => a.isScheduled && a.effectiveTriggerType === 'percent-complete');
      const scheduledDate = annotated.filter(a => a.isScheduled && a.effectiveTriggerType === 'date');

      let chosen = overdueItems[0] ?? dueItems[0];
      if (!chosen) {
        // Pick lowest-threshold upcoming % complete milestone
        const lowestPct = scheduledPct.sort((a, b) => (a.milestone.triggerPercent ?? 0) - (b.milestone.triggerPercent ?? 0))[0];
        // Pick soonest upcoming date milestone
        const soonestDate = scheduledDate
          .filter(a => a.milestone.dueDate)
          .sort((a, b) => new Date(a.milestone.dueDate!).getTime() - new Date(b.milestone.dueDate!).getTime())[0];
        chosen = lowestPct ?? soonestDate;
      }

      if (chosen) {
        const m = chosen.milestone;
        nextBillingEventAmount = m.amount;
        // Phase 10.2: use the milestone's effectiveTriggerType so a date-based deposit
        // on a % complete project correctly reports as 'date' (not 'percent-complete').
        nextBillingEventTriggerType = chosen.effectiveTriggerType;
        if (chosen.effectiveTriggerType === 'percent-complete') {
          nextBillingEventTriggerPercent = m.triggerPercent;
          // Expose the optional deadline date so the UI can render "At 75% · by 30 Sep"
          nextBillingEvent = m.dueDate ?? undefined;
        } else {
          nextBillingEvent = m.dueDate ?? undefined;
        }
      }

      // Fallback: if no milestones found (project not in budget mock data), use
      // the deterministic synthetic date from Phase 6 so the WIP table still shows data.
      if (!chosen) {
        const milestoneAmount = contractValue / 5;
        let daysOffset: number;
        if (index % 3 === 0) {
          daysOffset = -(10 + (index * 5) % 26);
        } else if (index % 3 === 1) {
          daysOffset = (index * 3) % 15;
        } else {
          daysOffset = 15 + (index * 7 + 3) % 30;
        }
        const d = new Date();
        d.setDate(d.getDate() + daysOffset);
        nextBillingEvent = d.toISOString().split('T')[0];
        nextBillingEventAmount = milestoneAmount;
        nextBillingEventTriggerType = 'date';
      }
    } else if (projectType === 'retainer') {
      // Retainers bill on the 1st of next month
      const d = new Date();
      d.setMonth(d.getMonth() + 1, 1);
      nextBillingEvent = d.toISOString().split('T')[0];
      nextBillingEventAmount = contractValue / 12;
      nextBillingEventTriggerType = 'date';
    }
    // T&M: no fixed billing event — bill on demand
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 6.2 — Under Billing / Over Billing
  //
  // T&M:       under = time done > time billed (wipAmount > 0)
  //            over  = billed more than time done (wipAmount < 0)
  //
  // Fixed Fee: under = billing event date has passed without an invoice raised
  //            over  = invoice raised before the billing event was earned
  //            In practice this maps cleanly to the WIP sign:
  //              wipAmount > 0 → earned more than billed → under billed
  //              wipAmount < 0 → billed more than earned → over billed
  // ─────────────────────────────────────────────────────────────────────────────
  const overBilling = wipAmount < 0 ? Math.abs(wipAmount) : 0;
  const underBilling = wipAmount > 0 ? wipAmount : 0;

  // Calculate aging based on WIP amount and project status
  let agingDays = 0;
  if (wipAmount > 0) {
    agingDays = Math.floor((index * 37 + 11) % 90) + 1;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // 6.3 / Phase 10.1 — Ready to Bill
  //
  // T&M:       wipAmount > £10k threshold and project is active
  //
  // Fixed Fee (Phase 10.1 rework): a billing event is Due or Overdue when:
  //   - Date milestone:         dueDate ≤ today and not yet invoiced (already filtered)
  //   - % Complete milestone:   project.billingTriggerType drives evaluation;
  //                             completionPercent >= triggerPercent → Due
  //                             optional deadline passed without threshold met → Overdue
  //
  // hasActionableFixedFeeMilestone is pre-computed above using the annotated array,
  // which already excludes paid/invoiced milestones and evaluates % complete thresholds
  // using project.billingTriggerType (not the removed per-milestone triggerType).
  // ─────────────────────────────────────────────────────────────────────────────
  let readyToBill: boolean;
  if (projectType === 'fixed-price') {
    // Phase 10.1: use hasActionableFixedFeeMilestone which is derived from the
    // annotated array (paid milestones excluded, % complete evaluated via project.billingTriggerType).
    readyToBill = hasActionableFixedFeeMilestone && earnedRevenue > amountBilled && project.statusId === 2;
  } else {
    readyToBill = wipAmount > 10000 && project.statusId === 2;
  }

  return {
    projectId: project.id,
    projectName: project.name,
    clientName: project.clientNames || 'Unknown Client',
    projectType,
    costsIncurred,
    profitEarned,
    amountBilled,
    wipAmount,
    agingDays,
    readyToBill,
    overBilling,
    underBilling,
    nextBillingEvent,
    nextBillingEventAmount,
    nextBillingEventTriggerType,
    nextBillingEventTriggerPercent,
  };
});

// Utilisation Data
export const mockUtilisationData: UtilisationData[] = mockEmployees.map(employee => {
  // Calculate total allocation from resource allocations
  const allocations = mockResourceAllocations.filter(ra => ra.employeeId === employee.id);
  const totalAllocation = allocations.reduce((sum, ra) => sum + (ra.allocationPercent ?? 0), 0);
  
  // Assume 40 hours per week, 52 weeks per year = 2080 hours per year
  const availableHours = 2080;
  const billableHours = (totalAllocation / 100) * availableHours;
  const utilisationPercent = (billableHours / availableHours) * 100;
  const targetPercent = 75; // Target 75% utilisation
  
  let status: 'under' | 'target' | 'over' = 'target';
  if (utilisationPercent < targetPercent - 10) status = 'under';
  else if (utilisationPercent > targetPercent + 10) status = 'over';
  
  return {
    employeeId: employee.id,
    employeeName: employee.name,
    role: employee.role,
    department: employee.department,
    billableHours,
    availableHours,
    utilisationPercent,
    targetPercent,
    status,
  };
});

// Portfolio Summary
export const mockPortfolioSummary: PortfolioSummary = {
  totalProjects: mockProjects.length,
  activeProjects: mockProjects.filter(p => p.statusId === 2).length,
  portfolioValue: mockProjects.reduce((sum, p) => sum + p.budgetRevenue, 0),
  totalWIP: mockProjects.reduce((sum, p) => sum + p.wipAmount, 0),
  avgMarginPercent: mockProjects.reduce((sum, p) => sum + p.marginPercent, 0) / mockProjects.length,
  avgUtilisationPercent: mockUtilisationData.reduce((sum, u) => sum + u.utilisationPercent, 0) / mockUtilisationData.length,
  projectsByStatus: {
    'estimate': mockProjects.filter(p => p.statusId === 1).length,
    'in-progress': mockProjects.filter(p => p.statusId === 2).length,
    'suspended': mockProjects.filter(p => p.statusId === 3).length,
    'completed': mockProjects.filter(p => p.statusId === 6).length,
    'cancelled': mockProjects.filter(p => p.statusId === 5).length,
  },
  projectsByHealth: {
    'green': mockProjects.filter(p => p.health === 'green').length,
    'amber': mockProjects.filter(p => p.health === 'amber').length,
    'red': mockProjects.filter(p => p.health === 'red').length,
  },
};

// Integration Status
export const mockIntegrationStatus: IntegrationStatus[] = [
  { service: 'access-dimensions', status: 'connected', lastSync: '2025-01-15T16:00:00Z' },
  { service: 'access-financials', status: 'syncing', lastSync: '2025-01-15T15:45:00Z' },
  { service: 'timesheet-system', status: 'connected', lastSync: '2025-01-15T16:15:00Z' },
  { service: 'resource-scheduling', status: 'error', lastSync: '2025-01-15T14:30:00Z', errorMessage: 'Authentication token expired' },
];

// Mock Tasks
export const mockTasks: Task[] = [
  // Project 1 - Digital Transformation Programme
  { id: 1001, projectId: 1, name: 'Requirements Gathering', description: 'Gather and document requirements', assigneeId: 3, status: 'completed', priority: 'high', dueDate: '2024-02-15', estimatedHours: 80, actualHours: 85 },
  { id: 1002, projectId: 1, name: 'Architecture Design', description: 'Design system architecture', assigneeId: 14, status: 'completed', priority: 'high', dueDate: '2024-03-01', estimatedHours: 120, actualHours: 115 },
  { id: 1003, projectId: 1, name: 'Frontend Development', description: 'Build React frontend', assigneeId: 4, status: 'in-progress', priority: 'high', dueDate: '2025-03-31', estimatedHours: 400, actualHours: 280 },
  { id: 1004, projectId: 1, name: 'Backend API Development', description: 'Build REST APIs', assigneeId: 5, status: 'in-progress', priority: 'high', dueDate: '2025-03-31', estimatedHours: 350, actualHours: 245 },
  { id: 1005, projectId: 1, name: 'Cloud Infrastructure Setup', description: 'Configure Azure resources', assigneeId: 10, status: 'in-progress', priority: 'medium', dueDate: '2025-02-28', estimatedHours: 160, actualHours: 120 },
  
  // Project 2 - Financial Systems Integration
  { id: 1006, projectId: 2, name: 'System Analysis', description: 'Analyze existing systems', assigneeId: 6, status: 'completed', priority: 'high', dueDate: '2024-10-15', estimatedHours: 60, actualHours: 65 },
  { id: 1007, projectId: 2, name: 'Integration Development', description: 'Build integration layer', assigneeId: 12, status: 'in-progress', priority: 'high', dueDate: '2025-02-28', estimatedHours: 200, actualHours: 145 },
  { id: 1008, projectId: 2, name: 'Data Migration', description: 'Migrate historical data', assigneeId: 18, status: 'in-progress', priority: 'medium', dueDate: '2025-03-15', estimatedHours: 80, actualHours: 45 },
  { id: 1009, projectId: 2, name: 'User Acceptance Testing', description: 'Conduct UAT with stakeholders', assigneeId: 2, status: 'todo', priority: 'high', dueDate: '2025-03-20', estimatedHours: 40, actualHours: 0 },
  
  // Project 4 - Legal Case Management System (Amber - has overdue tasks)
  { id: 1010, projectId: 4, name: 'Requirements Workshop', description: 'Workshop with legal team', assigneeId: 11, status: 'completed', priority: 'high', dueDate: '2024-10-15', estimatedHours: 24, actualHours: 28 },
  { id: 1011, projectId: 4, name: 'Database Design', description: 'Design case management database', assigneeId: 12, status: 'completed', priority: 'high', dueDate: '2024-11-30', estimatedHours: 60, actualHours: 72 },
  { id: 1012, projectId: 4, name: 'Case Workflow Implementation', description: 'Build case workflow engine', assigneeId: 12, status: 'in-progress', priority: 'critical', dueDate: '2025-01-10', estimatedHours: 120, actualHours: 95 },
  { id: 1013, projectId: 4, name: 'Document Management Module', description: 'Build document storage', assigneeId: 12, status: 'in-progress', priority: 'high', dueDate: '2025-01-05', estimatedHours: 80, actualHours: 65 },
  { id: 1014, projectId: 4, name: 'Security Audit', description: 'Conduct security review', assigneeId: 19, status: 'todo', priority: 'critical', dueDate: '2025-01-12', estimatedHours: 40, actualHours: 0 },
  
  // Project 7 - ERP Implementation (Red - multiple overdue tasks)
  { id: 1015, projectId: 7, name: 'Data Cleansing', description: 'Clean legacy data', assigneeId: 6, status: 'in-progress', priority: 'critical', dueDate: '2024-12-15', estimatedHours: 200, actualHours: 245 },
  { id: 1016, projectId: 7, name: 'Module Configuration', description: 'Configure ERP modules', assigneeId: 11, status: 'in-progress', priority: 'critical', dueDate: '2024-12-20', estimatedHours: 180, actualHours: 215 },
  { id: 1017, projectId: 7, name: 'User Training', description: 'Train end users', assigneeId: 15, status: 'in-progress', priority: 'high', dueDate: '2025-01-10', estimatedHours: 60, actualHours: 45 },
  { id: 1018, projectId: 7, name: 'Go-Live Preparation', description: 'Prepare for go-live', assigneeId: 1, status: 'todo', priority: 'critical', dueDate: '2025-01-20', estimatedHours: 80, actualHours: 0 },
  
  // Project 5 - Mobile App Development
  { id: 1019, projectId: 5, name: 'UI/UX Design', description: 'Design mobile app interface', assigneeId: 7, status: 'completed', priority: 'high', dueDate: '2024-10-15', estimatedHours: 100, actualHours: 110 },
  { id: 1020, projectId: 5, name: 'iOS Development', description: 'Build iOS app', assigneeId: 4, status: 'in-progress', priority: 'high', dueDate: '2025-02-15', estimatedHours: 200, actualHours: 165 },
  { id: 1021, projectId: 5, name: 'Android Development', description: 'Build Android app', assigneeId: 5, status: 'in-progress', priority: 'high', dueDate: '2025-02-15', estimatedHours: 200, actualHours: 170 },
  { id: 1022, projectId: 5, name: 'API Integration', description: 'Integrate with backend APIs', assigneeId: 4, status: 'in-progress', priority: 'medium', dueDate: '2025-02-20', estimatedHours: 80, actualHours: 55 },
  { id: 1023, projectId: 5, name: 'App Store Submission', description: 'Submit to app stores', assigneeId: 20, status: 'todo', priority: 'medium', dueDate: '2025-02-25', estimatedHours: 16, actualHours: 0 },
];

// Mock Milestones
export const mockMilestones: Milestone[] = [
  // Project 1 - Digital Transformation Programme
  { id: 2001, projectId: 1, name: 'Requirements Sign-off', dueDate: '2024-02-28', completedDate: '2024-02-25', status: 'completed', paymentAmount: 120000, paymentReceived: true },
  { id: 2002, projectId: 1, name: 'Architecture Approval', dueDate: '2024-03-31', completedDate: '2024-03-28', status: 'completed', paymentAmount: 150000, paymentReceived: true },
  { id: 2003, projectId: 1, name: 'MVP Release', dueDate: '2024-09-30', completedDate: '2024-10-05', status: 'completed', paymentAmount: 200000, paymentReceived: true },
  { id: 2004, projectId: 1, name: 'Beta Release', dueDate: '2025-03-31', status: 'pending', paymentAmount: 250000, paymentReceived: false },
  { id: 2005, projectId: 1, name: 'Production Go-Live', dueDate: '2025-06-30', status: 'pending', paymentAmount: 280000, paymentReceived: false },
  
  // Project 2 - Financial Systems Integration
  { id: 2006, projectId: 2, name: 'Analysis Complete', dueDate: '2024-10-31', completedDate: '2024-10-28', status: 'completed', paymentAmount: 90000, paymentReceived: true },
  { id: 2007, projectId: 2, name: 'Integration Testing', dueDate: '2025-02-28', status: 'pending', paymentAmount: 135000, paymentReceived: false },
  { id: 2008, projectId: 2, name: 'Go-Live', dueDate: '2025-03-31', status: 'pending', paymentAmount: 225000, paymentReceived: false },
  
  // Project 3 - Infrastructure Modernisation
  { id: 2009, projectId: 3, name: 'Infrastructure Design', dueDate: '2024-07-31', completedDate: '2024-07-28', status: 'completed', paymentAmount: 180000, paymentReceived: true },
  { id: 2010, projectId: 3, name: 'Phase 1 Migration', dueDate: '2024-11-30', completedDate: '2024-11-25', status: 'completed', paymentAmount: 270000, paymentReceived: true },
  { id: 2011, projectId: 3, name: 'Phase 2 Migration', dueDate: '2025-03-31', status: 'pending', paymentAmount: 270000, paymentReceived: false },
  { id: 2012, projectId: 3, name: 'Final Cutover', dueDate: '2025-05-31', status: 'pending', paymentAmount: 180000, paymentReceived: false },
  
  // Project 4 - Legal Case Management System (Amber - has overdue milestone)
  { id: 2013, projectId: 4, name: 'Requirements Approval', dueDate: '2024-11-15', completedDate: '2024-11-18', status: 'completed', paymentAmount: 76000, paymentReceived: true },
  { id: 2014, projectId: 4, name: 'Core Functionality Complete', dueDate: '2025-01-15', status: 'overdue', paymentAmount: 152000, paymentReceived: false },
  { id: 2015, projectId: 4, name: 'UAT Sign-off', dueDate: '2025-03-31', status: 'pending', paymentAmount: 76000, paymentReceived: false },
  { id: 2016, projectId: 4, name: 'Go-Live', dueDate: '2025-04-30', status: 'pending', paymentAmount: 76000, paymentReceived: false },
  
  // Project 5 - Mobile App Development
  { id: 2017, projectId: 5, name: 'Design Approval', dueDate: '2024-10-31', completedDate: '2024-10-28', status: 'completed', paymentAmount: 110000, paymentReceived: true },
  { id: 2018, projectId: 5, name: 'Alpha Release', dueDate: '2024-12-31', completedDate: '2025-01-05', status: 'completed', paymentAmount: 165000, paymentReceived: false },
  { id: 2019, projectId: 5, name: 'Beta Release', dueDate: '2025-02-15', status: 'pending', paymentAmount: 165000, paymentReceived: false },
  { id: 2020, projectId: 5, name: 'App Store Launch', dueDate: '2025-02-28', status: 'pending', paymentAmount: 110000, paymentReceived: false },
  
  // Project 7 - ERP Implementation (Red - multiple overdue milestones)
  { id: 2021, projectId: 7, name: 'Requirements Finalized', dueDate: '2024-06-30', completedDate: '2024-07-15', status: 'completed', paymentAmount: 150000, paymentReceived: true },
  { id: 2022, projectId: 7, name: 'Configuration Complete', dueDate: '2024-09-30', completedDate: '2024-10-20', status: 'completed', paymentAmount: 187500, paymentReceived: true },
  { id: 2023, projectId: 7, name: 'Data Migration Complete', dueDate: '2024-12-15', status: 'overdue', paymentAmount: 187500, paymentReceived: false },
  { id: 2024, projectId: 7, name: 'UAT Sign-off', dueDate: '2025-01-10', status: 'overdue', paymentAmount: 112500, paymentReceived: false },
  { id: 2025, projectId: 7, name: 'Go-Live', dueDate: '2025-01-31', status: 'pending', paymentAmount: 112500, paymentReceived: false },
];

// Mock time entries
export const mockTimeEntries = [
  { id: 3001, projectId: 1, employeeId: 4, date: '2025-01-13', hours: 8, billable: true, description: 'Frontend development', approved: true },
  { id: 3002, projectId: 1, employeeId: 4, date: '2025-01-14', hours: 7.5, billable: true, description: 'API integration', approved: true },
  { id: 3003, projectId: 2, employeeId: 2, date: '2025-01-13', hours: 6, billable: true, description: 'Client meeting and planning', approved: true },
  { id: 3004, projectId: 5, employeeId: 7, date: '2025-01-14', hours: 8, billable: true, description: 'UI design mockups', approved: false },
  { id: 3005, projectId: 8, employeeId: 11, date: '2025-01-15', hours: 5, billable: true, description: 'Testing and QA', approved: false },
];

// Mock expenses
export const mockExpenses = [
  { id: 4001, projectId: 1, employeeId: 1, date: '2025-01-10', amount: 1200, category: 'Travel', description: 'Client site visit - flights', billable: true, approved: true },
  { id: 4002, projectId: 1, employeeId: 1, date: '2025-01-10', amount: 450, category: 'Accommodation', description: 'Hotel - 3 nights', billable: true, approved: true },
  { id: 4003, projectId: 2, employeeId: 2, date: '2025-01-12', amount: 85, category: 'Software', description: 'Development tools license', billable: true, approved: true },
  { id: 4004, projectId: 5, employeeId: 7, date: '2025-01-14', amount: 299, category: 'Software', description: 'Design software subscription', billable: true, approved: false },
  { id: 4005, projectId: 4, employeeId: 6, date: '2025-01-15', amount: 150, category: 'Software', description: 'Development tools', billable: true, approved: false },
];

// Mock companies for multi-company support
export const mockCompanies: Company[] = [
  {
    id: 5001,
    name: 'Access UK Ltd',
    code: 'UK-HQ',
    instanceId: '9ab3b82aa3b145dbbd46b69d5b8cb994',
    status: 'active',
    description: 'UK headquarters and main operations',
    activeProjects: 12,
    employeeCount: 150,
    createdAt: new Date('2020-01-01'),
    updatedAt: new Date('2025-01-08'),
    registeredAddress: {
      line1: 'Access House',
      line2: '101 King Street',
      city: 'Manchester',
      county: 'Greater Manchester',
      postcode: 'M2 4DQ',
      country: 'United Kingdom',
    },
    vatNumber: 'GB987654321',
    companyRegistrationNumber: '12345678',
    bankDetails: {
      bankName: 'HSBC UK',
      accountName: 'Access UK Ltd',
      accountNumber: '12345678',
      sortCode: '40-47-84',
      iban: 'GB29NWBK60161331926819',
      swiftBic: 'HBUKGB4B',
    },
    phone: '+44 161 123 4567',
    email: 'accounts@accessuk.com',
    website: 'www.accessuk.com',
    logoUrl: '/logo-placeholder.png',
  },
  {
    id: 5002,
    name: 'Access Europe GmbH',
    code: 'EU-OPS',
    instanceId: 'inst-eu-002',
    status: 'active',
    description: 'European operations centre',
    activeProjects: 8,
    employeeCount: 75,
    createdAt: new Date('2021-03-15'),
    updatedAt: new Date('2025-01-08'),
  },
  {
    id: 5003,
    name: 'Access North America Inc',
    code: 'NA-OPS',
    instanceId: 'inst-na-003',
    status: 'active',
    description: 'North American operations',
    activeProjects: 6,
    employeeCount: 45,
    createdAt: new Date('2022-06-01'),
    updatedAt: new Date('2025-01-08'),
  },
  {
    id: 5004,
    name: 'Access APAC Pty Ltd',
    code: 'APAC-OPS',
    instanceId: 'inst-apac-004',
    status: 'inactive',
    description: 'Asia-Pacific operations (currently inactive)',
    activeProjects: 0,
    employeeCount: 12,
    createdAt: new Date('2023-01-01'),
    updatedAt: new Date('2024-12-01'),
  },
];

// Helper function to get company by ID
export const getCompanyById = (id: number) => mockCompanies.find(c => c.id === id);

// Helper function to get active companies
export const getActiveCompanies = () => mockCompanies.filter(c => c.status === 'active');
